/**
 * The only place geometry maths lives — `docs/property-maps-v2.md` §3.2.
 *
 * A shape drawn on a map becomes a number on a quote, so the client that
 * draws it and the server that stores it have to agree to the square foot.
 * Two implementations would not: they would agree for a while, and then one
 * of them would get a rounding fix. So there is one, here, and the server
 * recomputes every value it is sent rather than trusting it (§4.2).
 *
 * **Turf rather than PostGIS**, for the reason the migration gives: the
 * portability rule (plan §2.5 — clone, set `.env`, `npm run migrate`) forbids
 * depending on an extension, and nothing here needs a spatial index. Turf's
 * area is geodesic on a sphere, which is within about 0.3 % of the ellipsoid
 * at the size of a car park — well inside the ±1 % the spec accepts.
 *
 * Coordinates are GeoJSON order throughout: **`[longitude, latitude]`**, WGS84.
 * Getting that backwards is the classic bug, which is why `LngLat` is named
 * for its order rather than called `Point`.
 */
import { z } from 'zod';
import turfArea from '@turf/area';
import turfLength from '@turf/length';
import turfCircle from '@turf/circle';
import turfKinks from '@turf/kinks';
import { MARK_PALETTE } from './marks.js';
/** One square metre in square feet, exactly. */
const SQFT_PER_SQM = 10.763910416709722;
const FT_PER_M = 3.280839895013123;
const SQFT_PER_ACRE = 43_560;
const SQFT_PER_SQYD = 9;
/** Rounded to a hundredth, which is what the `numeric(14,2)` column holds. */
const round2 = (value) => Math.round(value * 100) / 100;
/**
 * The enclosed area of a polygon, in square feet.
 *
 * Turf wants a Feature or a geometry; it reads rings the GeoJSON way, so a
 * hole subtracts itself and we do not have to.
 */
export function areaSqFt(polygon) {
    return round2(turfArea(polygon) * SQFT_PER_SQM);
}
/**
 * Length in feet: a line's run, or a polygon's perimeter.
 *
 * Turf's `length` only walks a LineString, so a polygon's outer ring is
 * handed over as one — which is the perimeter, closing point included,
 * because a GeoJSON ring repeats its first point at the end.
 */
export function lengthFt(geometry) {
    const line = geometry.type === 'LineString'
        ? geometry
        : { type: 'LineString', coordinates: geometry.coordinates[0] ?? [] };
    if (line.coordinates.length < 2)
        return 0;
    return round2(turfLength(line, { units: 'feet' }));
}
/**
 * An axis-aligned rectangle through two opposite corners.
 *
 * Axis-aligned is a real constraint, not a shortcut: the map never rotates
 * (`dragRotate: false`, §9.2), so "axis-aligned in screen space" and
 * "axis-aligned in lng/lat" are the same thing, and a rectangle stays a
 * rectangle when somebody drags a corner. Rotated rectangles are listed as
 * out of scope in §14 for exactly this reason.
 */
export function rectPolygon(a, b) {
    const [west, east] = a[0] <= b[0] ? [a[0], b[0]] : [b[0], a[0]];
    const [south, north] = a[1] <= b[1] ? [a[1], b[1]] : [b[1], a[1]];
    return {
        type: 'Polygon',
        coordinates: [[
                [west, south], [east, south], [east, north], [west, north], [west, south],
            ]],
    };
}
/**
 * A circle as a 64-sided polygon.
 *
 * Stored as a polygon so every reader — the area maths, the renderer, the
 * print page — handles one shape; the original centre and radius live in the
 * feature's `shape` column so the editor can still drag it as a circle. 64
 * steps is under 0.1 % short of a true circle's area and is what the spec
 * names, so the client and the server cut it the same way.
 */
export function circlePolygon(center, radiusM) {
    const made = turfCircle(center, radiusM / 1000, { steps: 64, units: 'kilometers' });
    return made.geometry;
}
/** Square feet in another area unit. Not rounded — the formatter rounds. */
export function convertArea(sqft, toUnit) {
    if (toUnit === 'ac')
        return sqft / SQFT_PER_ACRE;
    if (toUnit === 'sq yd')
        return sqft / SQFT_PER_SQYD;
    return sqft;
}
/** A number with thousands separators and no more precision than it has. */
const withSeparators = (value, places) => value.toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places });
/**
 * What an area reads as — `21,780 sq ft`, `0.5 ac`.
 *
 * The preference wins where there is one; with none, square feet until a
 * quarter of an acre, then acres, because "43,560 sq ft" is a number nobody
 * pictures and "1 ac" is. Nothing is ever rounded to fewer than whole units:
 * a lawn is not `0 sq ft` because it is small.
 */
export function formatArea(sqft, pref = 'ft') {
    if (pref === 'ac' || (pref === 'ft' && sqft >= 0.25 * SQFT_PER_ACRE)) {
        return `${withSeparators(convertArea(sqft, 'ac'), 2)} ac`;
    }
    if (pref === 'yd')
        return `${withSeparators(Math.round(convertArea(sqft, 'sq yd')), 0)} sq yd`;
    return `${withSeparators(Math.round(sqft), 0)} sq ft`;
}
/** What a length reads as — `412 ft`. */
export function formatLength(ft) {
    return `${withSeparators(Math.round(ft), 0)} ft`;
}
/**
 * Which family a measurement type's unit belongs to.
 *
 * It decides which types a shape may be saved as: an area shape offers the
 * area types, a line offers the length ones. Anything else — `each`, `hour` —
 * is `other` and is never offered, because no shape measures it.
 */
export function unitFamily(unit) {
    const key = unit.trim().toLowerCase();
    /*
     * The abbreviations too — `sf` and `lf` are what an estimator types and
     * what the seeded catalog uses, and a type whose unit is `sf` reading as
     * `other` meant no shape could be saved as it and its total printed with
     * three decimals of square feet. A unit this does not recognise is `other`,
     * which is the safe answer, so the list only ever needs widening.
     */
    if (['sq ft', 'sqft', 'sq. ft.', 'sf', 'square feet', 'sq yd', 'sqyd', 'sy', 'square yards',
        'ac', 'acre', 'acres'].includes(key))
        return 'area';
    if (['lin ft', 'linft', 'lf', 'linear feet', 'ft', 'feet', 'lin yd', 'linear yards',
        'yd', 'yards'].includes(key))
        return 'length';
    return 'other';
}
/** Metres between two points, on a sphere. Used by the 5 km typo guard. */
export function metresBetween(a, b) {
    const R = 6_371_008.8;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(b[1] - a[1]);
    const dLng = toRad(a[0] - b[0]);
    const lat1 = toRad(a[1]);
    const lat2 = toRad(b[1]);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
/** Every coordinate in a geometry, flattened — for bounds and the guard. */
export function pointsOf(geometry) {
    if (geometry.type === 'Point')
        return [geometry.coordinates];
    if (geometry.type === 'LineString')
        return geometry.coordinates;
    return geometry.coordinates.flat();
}
/** How many coordinates a geometry carries, against the 5,000 cap. */
export const vertexCount = (geometry) => pointsOf(geometry).length;
/**
 * The box around a set of geometries, or null when there are none.
 *
 * `[west, south, east, north]`, the order GeoJSON and MapLibre both use. Here
 * rather than in the canvas because the server's 5 km typo guard asks the same
 * question of the same shapes (§4.2), and two implementations of "how far does
 * this drawing reach" is two answers.
 *
 * Anything that is not a geometry is skipped rather than throwing: this runs
 * over `jsonb` the database handed back, and one malformed row should cost a
 * shape, not the camera.
 */
export function bboxOfGeometries(geometries) {
    let west = Infinity;
    let south = Infinity;
    let east = -Infinity;
    let north = -Infinity;
    for (const candidate of geometries) {
        const parsed = geometrySchema.safeParse(candidate);
        if (!parsed.success)
            continue;
        for (const [lng, lat] of pointsOf(parsed.data)) {
            west = Math.min(west, lng);
            south = Math.min(south, lat);
            east = Math.max(east, lng);
            north = Math.max(north, lat);
        }
    }
    return west === Infinity ? null : [west, south, east, north];
}
/** Feet, for a radius or a side in the `shape` blob. */
export const metresToFeet = (m) => round2(m * FT_PER_M);
// ---------------------------------------------------------------------------
// The one schema both sides validate against
// ---------------------------------------------------------------------------
const lngLat = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
export const geometrySchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('Point'), coordinates: lngLat }),
    z.object({ type: z.literal('LineString'), coordinates: z.array(lngLat).min(2) }),
    /*
     * A ring needs four points because GeoJSON repeats the first at the end —
     * a triangle is five. Postgres cannot check the shape of a jsonb blob, so
     * this is the only thing standing between a malformed ring and a renderer
     * that throws on every page load afterwards.
     */
    z.object({ type: z.literal('Polygon'), coordinates: z.array(z.array(lngLat).min(4)).min(1) }),
]);
export const FEATURE_KINDS = ['area', 'rect', 'circle', 'line', 'marker', 'label'];
/** The glyphs a marker may wear (§3.2). */
export const GLYPHS = [
    'hazard', 'obstacle', 'drain', 'hydrant', 'gate', 'branch', 'pile', 'note',
];
export const LAYER_COLORS = [
    'blue', 'teal', 'indigo', 'green', 'amber', 'red', 'purple', 'slate',
];
/**
 * What each of the eight is, once (`property-maps-v2.md` §12 rule 1: "the
 * eight layer colours are tokens; components never contain a layer hex").
 *
 * **It is `MARK_PALETTE` narrowed, not a second list.** The marks palette is
 * already the ten colours a division or an equipment type may wear, enforced
 * by zod and by a CHECK, and the eight the mockup draws for a layer are eight
 * of those ten. A parallel table of the same hexes would be the drift
 * `shared/src/marks.ts` exists to prevent — and a layer dot beside a division
 * tile reading two slightly different greens is exactly how it would show up.
 */
export const LAYER_PALETTE = Object.fromEntries(LAYER_COLORS.map((name) => [name, MARK_PALETTE[name]]));
/**
 * What the editor may send for a shape.
 *
 * `areaSqFt` and `lengthFt` are **not** here: the server computes them from
 * the geometry every time, so a client cannot post a polygon of one size and
 * a number of another.
 */
export const featureInput = z.object({
    kind: z.enum(FEATURE_KINDS),
    geometry: geometrySchema,
    shape: z.record(z.string(), z.unknown()).nullish(),
    name: z.string().max(200).default(''),
    note: z.string().max(4000).nullish(),
    color: z.enum(LAYER_COLORS).nullish(),
    glyph: z.enum(GLYPHS).nullish(),
    photoFileId: z.string().uuid().nullish(),
});
/**
 * A geometry the kind can actually be.
 *
 * The CHECK constraint cannot reach inside the jsonb, so this is where "a
 * marker is a Point" is enforced. Called by the service on every write.
 */
/**
 * What is wrong with a shape, or nothing — `property-maps-v2.md` §9.3.
 *
 * **Both sides call this**, which is the point: the tools run it before a
 * shape is offered for saving so the drawing stays in edit and a toast can
 * say what to adjust, and `insertFeature` runs it again because a client is
 * not a validator. Two implementations of "is this drawable" is two answers.
 *
 * The checks, in the order somebody would notice them:
 *
 * **The kind and the geometry agree** — a line is a `LineString`, a marker a
 * `Point`, everything else a `Polygon`.
 *
 * **An area has three distinct corners.** Three clicks in the same place is a
 * polygon with no inside; `@turf/area` says 0 and the row would read `0 sq ft`
 * for ever.
 *
 * **No edge crosses another** (`@turf/kinks`). A bow-tie polygon has an area
 * Turf will happily compute and nobody can defend, and §9.3 names the words:
 * *Edges cross — adjust a point*.
 *
 * **Nothing is more than 5 km from the property** (§4.2's typo guard), when a
 * pin is given. A coordinate pair typed or pasted the wrong way round lands in
 * another state, and the shape that results is not worth keeping.
 */
export function geometryProblem(kind, geometry, pin) {
    if (!geometryFits(kind, geometry)) {
        return `A ${kind} cannot be a ${geometry.type}.`;
    }
    if (geometry.type === 'Polygon') {
        /* The ring repeats its first point to close; the corners are the rest. */
        const ring = geometry.coordinates[0] ?? [];
        const corners = new Set(ring.map(([lng, lat]) => `${lng},${lat}`));
        if (corners.size < 3)
            return 'An area needs at least three corners.';
    }
    if (geometry.type !== 'Point' && selfIntersects(geometry)) {
        return 'Edges cross — adjust a point.';
    }
    if (pin) {
        const far = pointsOf(geometry).some((point) => metresBetween(point, pin) > GUARD_METRES);
        if (far)
            return 'That is more than 5 km from the property.';
    }
    return null;
}
/** The 5 km typo guard (§4.2), shared with the server's own check. */
export const GUARD_METRES = 5_000;
/** Whether a line or a ring crosses itself. A point never does. */
export function selfIntersects(geometry) {
    /*
     * `@turf/kinks` wants a Feature; it returns the crossing *points*, and a
     * closed ring's own start/end is not one of them — which is why a polygon
     * can be asked directly rather than unrolled into segments here.
     */
    const found = turfKinks({ type: 'Feature', properties: {}, geometry });
    return (found.features?.length ?? 0) > 0;
}
export function geometryFits(kind, geometry) {
    if (kind === 'marker' || kind === 'label')
        return geometry.type === 'Point';
    if (kind === 'line')
        return geometry.type === 'LineString';
    return geometry.type === 'Polygon';
}
/** The two measured numbers a shape carries, or nulls where it has none. */
export function measuresOf(kind, geometry) {
    if (geometry.type === 'Polygon') {
        return { areaSqft: areaSqFt(geometry), lengthFt: lengthFt(geometry) };
    }
    if (geometry.type === 'LineString')
        return { areaSqft: null, lengthFt: lengthFt(geometry) };
    return { areaSqft: null, lengthFt: null };
}
/**
 * The scale bar's round distance, and how wide to draw it.
 *
 * Board 2 puts a 90px bar bottom-right of the canvas reading `50 ft`. The
 * number has to be round and the bar has to be as wide as that number
 * actually is, so the width is derived from the distance rather than the
 * other way round: pick the largest 1 · 2 · 5 step that fits, then measure
 * it. A bar labelled 50 ft and drawn 90px wide when 50 ft is 71px is a ruler
 * that lies.
 *
 * It lives here rather than on the client because the print page (§9.6) and
 * the crew app's full-screen map (§8) draw the same bar, and it is pure
 * arithmetic with nothing of a browser in it.
 */
export function scaleBar(mPerPx, maxPixels = 90) {
    const feetPerPixel = mPerPx * FT_PER_M;
    const most = feetPerPixel * maxPixels;
    /* Feet up to a mile, then miles — a crew reads a yard in feet and a
       snow route in miles, and nothing in between is worth a third unit. */
    if (most >= 5_280) {
        const miles = ladder(most / 5_280);
        return { label: `${miles} mi`, pixels: (miles * 5_280) / feetPerPixel };
    }
    const feet = ladder(most);
    return { label: `${feet} ft`, pixels: feet / feetPerPixel };
}
/** The largest 1 · 2 · 5 × 10^n at or below `most`, and never zero. */
function ladder(most) {
    if (!(most > 0) || !Number.isFinite(most))
        return 1;
    const power = 10 ** Math.floor(Math.log10(most));
    for (const step of [5, 2, 1]) {
        if (step * power <= most)
            return step * power;
    }
    return power / 2;
}
/**
 * How many metres one CSS pixel covers, which is a fact about Web Mercator
 * and the latitude you are looking at — not about the map library.
 */
export function metresPerPixel(latitude, zoom) {
    return (156_543.033_928 * Math.cos((latitude * Math.PI) / 180)) / 2 ** zoom;
}
//# sourceMappingURL=geo.js.map